import { connect } from "react-redux";
import Loader from "../../../Home/Loader/Loader";
import { Breadcrumb, Button, Card, Layout, notification } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Content } from "antd/es/layout/layout";
import GateEntryMetaDataForm from "./GateEntryMetaDataForm";
import { useEffect, useRef, useState } from "react";
import VisitorImage from "./VisitorImage";
import Signature from "../Documents/documentSignatureDetails/Signature";
import { createGateEntry } from "../../../redux/actions/API/gateEntry";
import { uploadImage } from "../../../redux/actions/API/imageUpload";
import store from "../../../redux/store";
import "./CreateGateEntry.css";

const INITIAL_FORM = {
  documentNumber: "",
  visitorName: "",
  visitorContact: "",
  visitorEmail: "",
  visitorCompany: "",
  idProofType: "",
  idProofNumber: "",
  purposeOfVisit: "",
  personToMeet: "",
  vehicleType: "",
  vehicleNumber: "",
  comments: "",
  securitySignatureUrl: "",
  seriesId: null,
};

interface ICreateGateEntryProps {
  companyId: any;
  userId: any;
  createGateEntry: Function;
  createGateEntryState: any;
  uploadImage: Function;
  visitorImageData: any;
  documentSignatureData: any;
}

const CreateGateEntry: React.FC<ICreateGateEntryProps> = ({
  companyId,
  userId,
  createGateEntry,
  createGateEntryState,
  uploadImage,
  visitorImageData,
  documentSignatureData,
}) => {
  const [gateEntryForm, setGateEntryForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const prevCreateStateRef = useRef<any>(null);

  const openNotificationWithIcon = (type: any, message: string) => {
    notification.open({
      message: message,
      type: type,
      duration: 3,
    });
  };

  // Watch for createGateEntry API response
  useEffect(() => {
    if (prevCreateStateRef.current?.loading && !createGateEntryState?.loading) {
      if (createGateEntryState?.error) {
        openNotificationWithIcon("error", createGateEntryState.error?.message || "Failed to create gate entry.");
        setLoading(false);
      } else {
        openNotificationWithIcon("success", createGateEntryState.data?.message || "Gate entry created successfully.");
        setLoading(false);
        const createdId = createGateEntryState.data?.data?.id;
        if (createdId) {
          navigate(`/previewGateEntry?documentNumber=${createdId}`);
        } else {
          navigate("/gate_entries");
        }
      }
    }
    prevCreateStateRef.current = createGateEntryState;
  }, [createGateEntryState]);

  const validateForm = (): boolean => {
    const { documentNumber, visitorName, visitorContact, purposeOfVisit, personToMeet, idProofType, idProofNumber } = gateEntryForm;
    if (!documentNumber?.trim()) {
      openNotificationWithIcon("error", "Document number is required.");
      return false;
    }
    if (!visitorName?.trim()) {
      openNotificationWithIcon("error", "Visitor name is required.");
      return false;
    }
    if (!visitorContact?.trim()) {
      openNotificationWithIcon("error", "Visitor contact is required.");
      return false;
    }
    if (!purposeOfVisit?.trim()) {
      openNotificationWithIcon("error", "Purpose of visit is required.");
      return false;
    }
    if (!personToMeet?.trim()) {
      openNotificationWithIcon("error", "Person to meet is required.");
      return false;
    }
    if (idProofType && !idProofNumber?.trim()) {
      openNotificationWithIcon("error", "ID proof number is required when ID proof type is selected.");
      return false;
    }
    if(!visitorImageData || visitorImageData.length <= 0){
      openNotificationWithIcon("error", "Visitor image is required.");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      // Upload visitor image
      let visitorImageUrl = "";
      if (visitorImageData && visitorImageData.length > 0) {
        const imageFile = visitorImageData[0];
        if (imageFile) {
          const formData = new FormData();
          formData.append("file", imageFile);

          await new Promise<void>((resolve) => {
            uploadImage(formData);
            const unsubscribe = store.subscribe(() => {
              const state = store.getState();
              const uploadedImageUrl = state.api.imageUpload.imageUrl;

              if (uploadedImageUrl) {
                visitorImageUrl = uploadedImageUrl;
                resolve();
                unsubscribe();
              }
            });
          });
        }
      }

      // Upload signature
      let signatureUrl = "";
      if (documentSignatureData && documentSignatureData.length > 0) {
        const signatureFile = documentSignatureData[0];
        if (typeof signatureFile === "string") {
          // Already a URL (default signature)
          signatureUrl = signatureFile;
        } else if (signatureFile) {
          const formData = new FormData();
          formData.append("file", signatureFile);

          await new Promise<void>((resolve) => {

            uploadImage(formData);
            const unsubscribe = store.subscribe(() => {
              const state = store.getState();
              const uploadedImageUrl = state.api.imageUpload.imageUrl;

              if (uploadedImageUrl) {
                signatureUrl = uploadedImageUrl;
                resolve();
                unsubscribe();
              }
            });
          });
        }
      }

      const form = gateEntryForm;
      const trimmedDocumentNumber = form.documentNumber?.trim() || "";

      const payload = {
        companyId: Number(companyId),
        userId: Number(userId),
        documentNumber: trimmedDocumentNumber,
        visitorName: form.visitorName?.trim() || "",
        visitorContact: form.visitorContact?.trim() || "",
        visitorEmail: form.visitorEmail?.trim() || null,
        visitorCompany: form.visitorCompany?.trim() || null,
        visitorImageUrl: visitorImageUrl,
        idProofType: form.idProofType?.trim() || null,
        idProofNumber: form.idProofNumber?.trim() || null,
        purposeOfVisit: form.purposeOfVisit?.trim() || "",
        personToMeet: form.personToMeet?.trim() || "",
        vehicleType: form.vehicleType?.trim() || null,
        vehicleNumber: form.vehicleNumber?.trim() || null,
        comments: form.comments?.trim() || null,
        seriesId: form.seriesId || 0,
        securitySignatureUrl: signatureUrl,
      };

      createGateEntry(payload);
    } catch (error) {
      console.error("Error during gate entry submission:", error);
      openNotificationWithIcon("error", "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <Loader loading={loading} />
      <Layout style={{ padding: "15px" }}>
        <Breadcrumb items={[{ title: "Gate Entry" }]} />
        <div className="divDocuments mt-10">
          <div
            onClick={() => navigate(-1)}
            className="linkToDocs"
            style={{ cursor: "pointer" }}
          >
            <p className="textDocument">
              <ArrowLeftOutlined /> Back to Entries
            </p>
          </div>
        </div>
        <Content style={{ overflow: "auto" }}>
          <Card className="cardForm mb-10">
            <GateEntryMetaDataForm
              gateEntryForm={gateEntryForm}
              setGateEntryForm={setGateEntryForm}
            />
          </Card>
          <div className="mt-10">
            <div className="flexBox">
              <VisitorImage />
              <Signature />
            </div>
          </div>
          <div className="mt-10" style={{ textAlign: "right" }}>
            <Button className="action-btn save-btn" type="primary" onClick={handleSave} size="large">
              Save
            </Button>
          </div>
        </Content>
      </Layout>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  companyId: state.api.login?.data?.companyId,
  userId: state.api.login?.data?.id,
  createGateEntryState: state.api.createGateEntry,
  visitorImageData: state.ui.createDocumentUIData?.attachments,
  documentSignatureData: state.ui.createDocumentUIData?.signature,
});

const mapDispatchToProps = (dispatch: any) => ({
  createGateEntry: (payload: any) => dispatch(createGateEntry(payload)),
  uploadImage: (payload: any) => dispatch(uploadImage(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateGateEntry);